// Chord detection from score CONTENT — slice 3 of the transcription path.
//
// An AI transcription arrives with zero chord symbols, and the whole band
// machinery (comp, bass, drums, the analysis lane) hangs off the harmony
// lane. The changes are recoverable from what the rhythm section actually
// played: per HALF-BAR cell (the repertoire's harmonic-rhythm unit) the
// sounding pitch classes are weighted by duration (bass register doubled —
// roots ground the harmony), matched against a curated template set mapped
// onto the app's own chord catalogue, and smoothed by a full Viterbi over
// the cell grid so a passing tone can't flicker the symbol. The result is
// written in the chart's own conventions: a chord holds until the next
// symbol (empty bars inherit), and a mid-bar change restates the carried
// chord at the bar start because a measure's chords lay out from beat 1.
//
// Templates rather than the raw 24-type catalogue: detection wants PRIORS
// (a m7 is likelier than a m7♯5) and tolerance (a 9 over a m7 cell is still
// "m7"), and the catalogue's extension types are deliberately rootless
// stacks. Each template declares required tones (the notes that must sound
// for the name to be honest) and optional colours, and maps to the
// catalogue id that PLAYS it — the same bare-triad→seventh convention the
// iReal and openbook importers use.
//
// Part ROLES ride along: `detectPartRoles` proposes lead/comp from the
// parts' own statistics (a grand staff or chordal texture comps; the
// busiest melodic single-staff part leads), so the lead-sheet projection
// carries the HEAD rather than whichever part the exporter listed first.
//
// Pure and deterministic throughout; callers commit.

import type { KeyContext } from '../notes';
import { spellNoteInKey } from '../notes';
import { chordFromType } from '../chart';
import type { ChordTypeId } from '../chords';
import { uid } from '../song';
import { fractionToNumber } from './duration';
import { midiOf } from './pitch';
import { voiceTiming } from './timing';
import { formSpans } from './transcription';
import type { HarmonySymbol, KeySignature, Part, PartRole, Score } from './types';

// ---------------------------------------------------------------------------
// Templates
// ---------------------------------------------------------------------------

interface ChordTemplate {
  /** The catalogue type that plays this symbol. */
  chordType: ChordTypeId;
  /** Display quality in the chart's spelling ('' = bare major triad). */
  quality: string;
  /** Semitones above the root that MUST sound for the name to be honest. */
  required: number[];
  /** Members that may sound and still count as chord tones. */
  optional: number[];
  /** Small prior separating everyday types from exotic ones on near-ties. */
  prior: number;
}

// 9ths/13ths ride as optional colours on the plain types; the bare triads
// map to sevenths exactly as the iReal/openbook importers rule (a triad must
// gain a fourth voice to play, and takes the 7th).
const TEMPLATES: ChordTemplate[] = [
  { chordType: 'maj7', quality: '', required: [0, 4], optional: [7], prior: 0.0 },
  { chordType: 'maj7', quality: 'maj7', required: [0, 4, 11], optional: [7, 2], prior: 0.02 },
  { chordType: 'maj6', quality: '6', required: [0, 4, 9], optional: [7, 2], prior: 0.0 },
  { chordType: 'min7', quality: 'm', required: [0, 3], optional: [7], prior: 0.0 },
  { chordType: 'min7', quality: 'm7', required: [0, 3, 10], optional: [7, 2, 5], prior: 0.02 },
  { chordType: 'min6', quality: 'm6', required: [0, 3, 9], optional: [7, 2], prior: 0.0 },
  { chordType: 'minmaj7', quality: 'm(maj7)', required: [0, 3, 11], optional: [7, 2], prior: -0.02 },
  { chordType: 'dom7', quality: '7', required: [0, 4, 10], optional: [7, 2, 9], prior: 0.02 },
  { chordType: 'dom7sus4', quality: '7sus4', required: [0, 5, 10], optional: [7, 2], prior: -0.05 },
  { chordType: 'dom7b9', quality: '7b9', required: [0, 4, 10, 1], optional: [7], prior: 0.0 },
  { chordType: 'dom7s9', quality: '7#9', required: [0, 4, 10, 3], optional: [7], prior: -0.02 },
  { chordType: 'dom7s5', quality: '7#5', required: [0, 4, 8, 10], optional: [], prior: -0.02 },
  { chordType: 'dom7b5', quality: '7b5', required: [0, 4, 6, 10], optional: [], prior: -0.02 },
  { chordType: 'min7b5', quality: 'm7b5', required: [0, 3, 6, 10], optional: [], prior: 0.0 },
  { chordType: 'dim7', quality: 'o7', required: [0, 3, 6, 9], optional: [], prior: -0.01 },
];

/** Broad quality family — the unit the audit scores (a maj7 heard as a 6 is
 *  a defensible reading; a maj7 heard as a dominant is not). */
export function chordFamilyOf(chordType: ChordTypeId): 'maj' | 'min' | 'dom' | 'halfdim' | 'dim' {
  if (chordType.startsWith('min7b5')) return 'halfdim';
  if (chordType.startsWith('dim')) return 'dim';
  if (chordType.startsWith('min')) return 'min';
  if (chordType.startsWith('dom')) return 'dom';
  return 'maj';
}

// ---------------------------------------------------------------------------
// Cells
// ---------------------------------------------------------------------------

interface Cell {
  /** Bar this cell opens in. */
  barIndex: number;
  /** Onset within the bar, in that bar's DENOMINATOR beat units. */
  beatInBar: number;
  /** Absolute quarters. */
  startQ: number;
  endQ: number;
  /** Duration-weighted pitch-class mass (bass register doubled). */
  weights: number[];
  total: number;
  /** Pitch class of the lowest attack in the cell, if any bass sounds. */
  bassPc: number | null;
  /** Distinct pitch classes carrying real mass. */
  distinct: number;
  /** Unweighted sounding note-time in the cell (simultaneity measure). */
  noteQ: number;
}

const BASS_MIDI = 50;

/** Cut the form into half-bar cells and aggregate each cell's chroma exactly
 *  (no eighth-window approximation — cells may sit on fractional quarters in
 *  odd meters). */
function buildCells(score: Score): Cell[] {
  const spans = formSpans(score.form);
  let timeSig: [number, number] = score.form[0]?.timeSig ?? [4, 4];
  const cells: Cell[] = [];
  score.form.forEach((m, mi) => {
    timeSig = m.timeSig ?? timeSig;
    const start = fractionToNumber(spans[mi].start);
    const end = fractionToNumber(spans[mi].end);
    const barQ = end - start;
    const beatsPerQuarter = timeSig[1] / 4;
    const boundaries =
      barQ > 2 && !m.pickupBeats ? [start, start + barQ / 2, end] : [start, end];
    for (let i = 0; i + 1 < boundaries.length; i++) {
      cells.push({
        barIndex: mi,
        beatInBar: (boundaries[i] - start) * beatsPerQuarter,
        startQ: boundaries[i],
        endQ: boundaries[i + 1],
        weights: new Array<number>(12).fill(0),
        total: 0,
        bassPc: null,
        distinct: 0,
        noteQ: 0,
      });
    }
  });

  // One pass over the content, distributing each note's sounding span across
  // the cells it overlaps. ANTICIPATION FOLD: this music states the coming
  // chord on the last beat of the bar before it (the latin ponche, a swing
  // push, a walking approach tone) while the CHART writes the change on the
  // barline — so half of any mass sounding in a bar's final quarter is
  // credited to the next bar's first cell. Without it the correctly-barred
  // George's Dilemma smeared every pushed change across the previous bar's
  // second cell (the E7/Amaj7 synonym drift); with it the synthesis audit's
  // approach tones also land beside the chord they point at.
  const lastCellOfBar = new Map<number, Cell>();
  for (const cell of cells) lastCellOfBar.set(cell.barIndex, cell);
  const bassAttacks: { start: number; midi: number }[] = [];
  for (const part of score.parts) {
    part.measures.forEach((pm, mi) => {
      if (!spans[mi]) return;
      const barStart = fractionToNumber(spans[mi].start);
      for (const voice of pm.voices) {
        for (const timed of voiceTiming(voice)) {
          const ev = timed.event;
          if (ev.kind !== 'note' || ev.grace) continue;
          const s = barStart + fractionToNumber(timed.start);
          const e = s + fractionToNumber(timed.duration);
          for (const head of ev.notes) {
            const midi = midiOf(head.pitch);
            const pc = ((midi % 12) + 12) % 12;
            const isAttack = head.tie !== 'stop' && head.tie !== 'both';
            if (isAttack && midi < BASS_MIDI) bassAttacks.push({ start: s, midi });
            const w = midi < BASS_MIDI ? 2 : 1;
            cells.forEach((cell, ci) => {
              const overlap = Math.min(e, cell.endQ) - Math.max(s, cell.startQ);
              if (overlap <= 0) return;
              cell.noteQ += overlap;
              const next = cells[ci + 1];
              const isBarFinal = lastCellOfBar.get(cell.barIndex) === cell;
              const tail =
                isBarFinal && next
                  ? Math.max(0, Math.min(e, cell.endQ) - Math.max(s, cell.endQ - 1))
                  : 0;
              const folded = tail * ANTICIPATION_FOLD;
              cell.weights[pc] += (overlap - folded) * w;
              if (folded > 0) next.weights[pc] += folded * w;
            });
          }
        }
      }
    });
  }
  for (const cell of cells) {
    cell.total = cell.weights.reduce((s, x) => s + x, 0);
    cell.distinct = cell.weights.filter((w) => w > 0.05 * cell.total).length;
    // The bass note nearest the cell's start names the likeliest root. A
    // BARLINE cell accepts an attack up to a beat early — the anticipated
    // bass sounds the new bar's root on the push and often plays nothing on
    // the "one" it is ringing through; mid-bar cells keep the tight eighth.
    const earlyWindow = cell.beatInBar === 0 ? 1.0 : 0.5;
    let best: { start: number; midi: number } | null = null;
    for (const a of bassAttacks) {
      if (a.start < cell.startQ - earlyWindow || a.start >= cell.endQ) continue;
      if (!best || Math.abs(a.start - cell.startQ) < Math.abs(best.start - cell.startQ)) best = a;
      else if (Math.abs(a.start - cell.startQ) === Math.abs(best.start - cell.startQ)) {
        if (a.midi < best.midi) best = a;
      }
    }
    cell.bassPc = best ? ((best.midi % 12) + 12) % 12 : null;
  }
  return cells;
}

// ---------------------------------------------------------------------------
// Emission scoring + Viterbi
// ---------------------------------------------------------------------------

/** One (root × template) hypothesis; index 0 is the silence state. */
interface StateDef {
  root: number;
  template: ChordTemplate;
}

const MISS_WEIGHT = 1.2;
const COVER_WEIGHT = 0.5;
/** Pull toward the detected key's TONIC chord on cells whose evidence cannot
 *  name a chord (a {root, neighbor} dyad bar of a latin vamp — the real file's
 *  written C#m7 bars contain literally C# and D). Scaled by (1 − evidence):
 *  a four-pc cell feels none of it. This is what a reader does: ambiguity
 *  resolves to the running tonality, not to whichever neighbor the bass
 *  passed through. */
const TONIC_WEIGHT = 0.3;
/** The bass names the root — but gently: latin and walking basses are the
 *  most mobile voice on the page, and a fifth-in-the-bass half-bar must not
 *  re-root the chord — but firmly enough to break pitch-class SYNONYMS,
 *  which pc mass alone never can: the C#m7(11) vamp's set IS Amaj9's set
 *  (the Lab's synonyms exhibit), and only the player's C# under it says
 *  which chord it is. 0.25 lost that vote to the relative major; 0.4 wins
 *  it while the raised CHANGE_COST keeps the fifth-in-the-bass half-bars
 *  from re-rooting (the G#7sus4 flicker this constant caused at 0.35 with
 *  weak smoothing). The bonus is also SCALED BY EVIDENCE: on a dyad cell
 *  the bass is exactly the misleading voice (neighbors and fifths), so its
 *  vote fades as the cell thins. */
const BASS_WEIGHT = 0.4;
/** Cost of changing chord between cells — the smoothing knob. High enough
 *  that a passing tone or an inversion can't flip one cell, low enough that
 *  a real ii–V moving every 2 beats still tracks. */
const CHANGE_COST = 0.45;
/** Share of a bar's final-quarter mass credited to the next bar's first
 *  cell (see the fold comment in buildCells). Calibrated against the
 *  synthesis audit, whose 2-beat harmonic rhythm owns its bars' final
 *  quarters: 0.5 holds the audit's 0.90 root pin, 0.75 drops it to 0.878 —
 *  and a mid-bar change SURCHARGE fails the same way (1.6 → 0.876), so the
 *  push is absorbed by evidence, never by taxing real mid-bar changes. */
const ANTICIPATION_FOLD = 0.5;
const SILENCE_EMISSION = 0.1;
const SILENT_TOTAL = 1e-6;

interface EmissionContext {
  /** Tonic pitch class of the detected key, when one is known. */
  tonicPc: number | null;
  tonicMode: 'major' | 'minor';
}

function emission(cell: Cell, state: StateDef, ctx: EmissionContext): number {
  if (cell.total < SILENT_TOTAL) return -0.5; // silence prefers the N.C. state
  const { root, template } = state;
  const member = new Set([...template.required, ...template.optional].map((i) => (root + i) % 12));
  let hit = 0;
  for (const pc of member) hit += cell.weights[pc];
  const miss = cell.total - hit;
  const presence = 0.04 * cell.total;
  const covered = template.required.filter((i) => cell.weights[(root + i) % 12] > presence).length;
  const cover = covered / template.required.length;
  const bass = cell.bassPc !== null && cell.bassPc === root ? 1 : 0;
  // How much this cell can say on its own: 2 distinct pcs say nothing (0),
  // 4+ speak for themselves (1).
  const evidence = Math.max(0, Math.min(1, (cell.distinct - 2) / 2));
  const family = chordFamilyOf(template.chordType);
  const isTonic =
    ctx.tonicPc !== null &&
    root === ctx.tonicPc &&
    (ctx.tonicMode === 'minor' ? family === 'min' : family === 'maj');
  return (
    (hit - MISS_WEIGHT * miss) / cell.total +
    COVER_WEIGHT * cover +
    BASS_WEIGHT * bass * (0.3 + 0.7 * evidence) +
    (isTonic ? TONIC_WEIGHT * (1 - evidence) : 0) +
    template.prior
  );
}

/** Pitch class of a key's tonic name ("C#" → 1, "Eb" → 3). */
function tonicPcOf(key: KeySignature): number | null {
  const m = key.tonic.trim().match(/^([A-Ga-g])([#b]*)$/);
  if (!m) return null;
  const base: Record<string, number> = { C: 0, D: 2, E: 4, F: 5, G: 7, A: 9, B: 11 };
  let pc = base[m[1].toUpperCase()];
  for (const ch of m[2]) pc += ch === '#' ? 1 : -1;
  return ((pc % 12) + 12) % 12;
}

export interface DetectedHarmony {
  /** One HarmonySymbol[] per form measure, in chart conventions. */
  harmony: HarmonySymbol[][];
  /** Distinct symbols written. */
  symbols: number;
}

export interface DetectHarmonyOptions {
  /** Spell roots for this key (accidental preference); default sharp side. */
  key?: KeySignature | null;
}

/**
 * Detect the chord changes a score's own content states. Returns null with no
 * side channel when the content cannot honestly carry chords — too little of
 * it, or too monophonic (a bare melody names no harmony; guessing would make
 * a missing decision inaudible).
 */
export function detectHarmony(
  score: Score,
  options: DetectHarmonyOptions = {},
): { ok: true; result: DetectedHarmony } | { ok: false; reason: string } {
  const cells = buildCells(score);
  const sounding = cells.filter((c) => c.total > SILENT_TOTAL);
  if (sounding.length < 4) return { ok: false, reason: 'too little content' };
  // Simultaneity, not per-cell pc variety, is what says "chords sound here":
  // a fast melody sweeps four pitch classes through a cell while never
  // sounding two at once, and a bare line names no harmony.
  const coveredQ = sounding.reduce((s, c) => s + (c.endQ - c.startQ), 0);
  const noteQ = sounding.reduce((s, c) => s + c.noteQ, 0);
  if (noteQ / coveredQ < 1.5) {
    return { ok: false, reason: 'content too monophonic to name chords' };
  }

  // Full state space: every root × template, plus silence at index 0. Small
  // enough (181 states) that no beam is needed — the DP is exact.
  const states: (StateDef | null)[] = [null];
  for (let root = 0; root < 12; root++) {
    for (const template of TEMPLATES) states.push({ root, template });
  }
  const S = states.length;
  const tonicPc = options.key ? tonicPcOf(options.key) : null;
  const ctx: EmissionContext = { tonicPc, tonicMode: options.key?.mode ?? 'major' };
  const score2 = (cell: Cell, si: number): number =>
    si === 0
      ? cell.total < SILENT_TOTAL
        ? SILENCE_EMISSION
        : -SILENCE_EMISSION
      : emission(cell, states[si]!, ctx);

  const dp = new Float64Array(S).fill(-Infinity);
  const back: Int32Array[] = [];
  for (let si = 0; si < S; si++) dp[si] = score2(cells[0], si);
  for (let ci = 1; ci < cells.length; ci++) {
    const bk = new Int32Array(S);
    let bestPrev = 0;
    for (let si = 1; si < S; si++) if (dp[si] > dp[bestPrev]) bestPrev = si;
    const next = new Float64Array(S);
    for (let si = 0; si < S; si++) {
      const stay = dp[si];
      const change = dp[bestPrev] - CHANGE_COST;
      if (stay >= change) {
        next[si] = stay + score2(cells[ci], si);
        bk[si] = si;
      } else {
        next[si] = change + score2(cells[ci], si);
        bk[si] = bestPrev;
      }
    }
    back.push(bk);
    next.forEach((v, i) => (dp[i] = v));
  }
  let cur = 0;
  for (let si = 1; si < S; si++) if (dp[si] > dp[cur]) cur = si;
  const path = new Array<number>(cells.length);
  for (let ci = cells.length - 1; ci >= 0; ci--) {
    path[ci] = cur;
    if (ci > 0) cur = back[ci - 1][cur];
  }

  // Interior silence sustains the running chord (the band rings through a
  // break); leading silence stays unwritten so the bars are honestly empty.
  let running = 0;
  for (let ci = 0; ci < cells.length; ci++) {
    if (path[ci] === 0 && running !== 0) path[ci] = running;
    running = path[ci];
  }

  // Changes → per-bar symbols in chart conventions. A chord spanning bars is
  // written once (empty bars inherit at playback and read as "keep playing");
  // a mid-bar change restates the carried chord at the bar's start because a
  // measure's chords lay out sequentially from beat 1.
  const keyContext: KeyContext | null = options.key
    ? { tonic: options.key.tonic, mode: options.key.mode }
    : null;
  const rootName = (pc: number) => spellNoteInKey(pc, keyContext);
  let timeSig: [number, number] = score.form[0]?.timeSig ?? [4, 4];
  const dens = score.form.map((m) => {
    timeSig = m.timeSig ?? timeSig;
    return timeSig;
  });
  const harmony: HarmonySymbol[][] = score.form.map(() => []);
  let symbols = 0;
  const noChord = (beats: number): HarmonySymbol => ({
    id: uid('c'),
    root: '',
    quality: '',
    chordType: null,
    beats,
    noChord: true,
    symbol: 'N.C.',
  });

  interface Anchor {
    barIndex: number;
    beatInBar: number;
    state: number;
  }
  const anchors: Anchor[] = [];
  let prev = -1;
  cells.forEach((cell, ci) => {
    const st = path[ci];
    if (st === prev) return;
    if (st !== 0) anchors.push({ barIndex: cell.barIndex, beatInBar: cell.beatInBar, state: st });
    prev = st;
  });

  anchors.forEach((a, ai) => {
    const def = states[a.state]!;
    const sig = dens[a.barIndex];
    const barBeats = score.form[a.barIndex].pickupBeats ?? sig[0];
    const next = anchors[ai + 1];
    if (a.beatInBar > 0 && harmony[a.barIndex].length === 0) {
      if (ai > 0) {
        // Restate the carried chord at the bar start — a measure's chords
        // lay out from beat 1, so a bar opening mid-span must say so.
        const prevDef = states[anchors[ai - 1].state]!;
        harmony[a.barIndex].push(
          chordFromType(rootName(prevDef.root), prevDef.template.chordType, a.beatInBar, {
            quality: prevDef.template.quality,
          }),
        );
      } else {
        // The music's very first chord lands mid-bar: the front of the bar
        // is honestly silent, and only an explicit N.C. can hold it open.
        harmony[a.barIndex].push(noChord(a.beatInBar));
      }
    }
    const beatsToBarEnd = barBeats - a.beatInBar;
    const sameBarNext = next && next.barIndex === a.barIndex ? next.beatInBar : null;
    const beats = sameBarNext !== null ? sameBarNext - a.beatInBar : beatsToBarEnd;
    harmony[a.barIndex].push(
      chordFromType(rootName(def.root), def.template.chordType, beats, {
        quality: def.template.quality,
      }),
    );
    symbols++;
  });

  if (symbols === 0) return { ok: false, reason: 'no chords could be named' };
  return { ok: true, result: { harmony, symbols } };
}

// ---------------------------------------------------------------------------
// Part roles
// ---------------------------------------------------------------------------

/**
 * Propose lead/comp roles from the parts' own statistics, so the lead-sheet
 * projection (and the workbench melody channel) carries the HEAD rather than
 * whichever part the exporter listed first. Returns an empty map when any
 * part already carries a role — authored roles are never second-guessed.
 */
export function detectPartRoles(score: Score): Map<string, PartRole> {
  const out = new Map<string, PartRole>();
  if (score.parts.length < 2) return out;
  if (score.parts.some((p) => p.role !== undefined)) return out;

  // COMP is chordal texture: a grand staff, real stacks (3+ noteheads), or
  // near-constant double-stops. A harmonized horn line is dyads on well under
  // that — the muscriptor tenor carries the HEAD in dyads where the model
  // heard both horns, and must not read as comping.
  const stats = score.parts.map((part) => partStats(part, score));
  stats.forEach((s, i) => {
    if (score.parts[i].staves.length >= 2 || s.stackShare >= 0.25 || s.dyadShare >= 0.7) {
      out.set(score.parts[i].id, 'comp');
    }
  });
  const melodic = stats
    .map((s, i) => ({ s, i }))
    .filter(({ i }) => !out.has(score.parts[i].id))
    .sort((a, b) => b.s.soundingQ - a.s.soundingQ);
  if (melodic.length > 0 && melodic[0].s.soundingQ > 0) {
    out.set(score.parts[melodic[0].i].id, 'lead');
  }
  return out;
}

function partStats(
  part: Part,
  score: Score,
): { soundingQ: number; stackShare: number; dyadShare: number } {
  const spans = formSpans(score.form);
  let soundingQ = 0;
  let events = 0;
  let stacks = 0;
  let dyads = 0;
  part.measures.forEach((pm, mi) => {
    if (!spans[mi]) return;
    for (const voice of pm.voices) {
      for (const timed of voiceTiming(voice)) {
        const ev = timed.event;
        if (ev.kind !== 'note' || ev.grace) continue;
        events++;
        soundingQ += fractionToNumber(timed.duration);
        if (ev.notes.length >= 3) stacks++;
        if (ev.notes.length >= 2) dyads++;
      }
    }
  });
  return {
    soundingQ,
    stackShare: events === 0 ? 0 : stacks / events,
    dyadShare: events === 0 ? 0 : dyads / events,
  };
}
